import type { Theme } from '@material-ui/core'
import { makeStyles, Popover, useTheme } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import HelpIcon from '@material-ui/icons/Help'
import RemoveRedEyeIcon from '@material-ui/icons/RemoveRedEye'
import classNames from 'classnames'
import React, { useState } from 'react'
import type { Size } from 'react-sizeme'
import Skeleton from '@paintscout/ui/components/Skeleton'
import Divider from '@paintscout/ui/components/Divider'
import StatCard from '@paintscout/ui/components/StatCard'
import Button from '@paintscout/ui/components/Button'
import DonutChart from '@paintscout/ui/components/GraphCard/DonutChart'
import Grid from '@paintscout/ui/components/Grid/index'
import Tooltip from '@paintscout/ui/components/Tooltip'
import Typography from '@paintscout/ui/components/Typography'
import type { StatCardGamifiedProps } from '../StatCardGamified'
import GamifiedCallout from '../GamifiedCallout'

const useStyles = makeStyles((theme: Theme) => ({
  root: (props) => ({
    padding: theme.spacing(),
    ...(props.corporateView && {
      height: '301.81px'
    })
  }),
  content: (props) => ({
    position: 'relative',
    width: '100%',
    padding: theme.spacing(),
    paddingTop: 0,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    justifyContent: 'space-evenly',
    display: 'flex',
    ...(props.corporateView && {
      height: '301.81px'
    })
  }),
  leftContainer: {
    flexBasis: '55%',
    paddingRight: theme.spacing(2),
    borderRight: '1px solid #E0E0E0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative'
  },
  rightContainer: (props: StatCardMainGamifiedProps) => {
    const showDashboardStandings = props.showGamifiedCallouts ?? false
    if (props.corporateView) {
      return {
        flexBasis: '45%',
        padding: `${theme.spacing()}px ${theme.spacing(2)}px`,
        marginTop: theme.spacing()
      }
    } else {
      return {
        flexBasis: '45%',
        padding: showDashboardStandings ? `${theme.spacing()}px ${theme.spacing(2)}px` : `${0}px ${theme.spacing(2)}px`,
        marginTop: showDashboardStandings ? theme.spacing(2) : theme.spacing()
      }
    }
  },
  primaryValue: {
    fontSize: theme.typography.pxToRem(72),
    fontWeight: 500,
    marginBottom: theme.spacing(0.25)
  },
  infoButton: (props) => {
    const showDashboardStandings = props?.showGamifiedCallouts ?? false
    return {
      position: 'absolute',
      right: 0,
      top: showDashboardStandings ? theme.spacing(0.3) : theme.spacing(0.4)
    }
  },
  statExtrasValue: {
    fontWeight: 500
  },
  gamifiedCalloutWrapper: {
    marginRight: theme.spacing(-2),
    marginBottom: theme.spacing(2.5),
    marginTop: theme.spacing(2.5)
  },
  popoverPaper: {},
  donutTitle: {
    textAlign: 'left',
    whiteSpace: 'nowrap',
    fontWeight: 400
  },
  helpIcon: {
    color: theme.paintscout.palette.primary,
    fontSize: '1rem'
  },
  horizontalDivider: (props) => ({
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    ...(props.corporateView && {
      marginTop: theme.spacing(2.25),
      marginBottom: theme.spacing(2.25)
    })
  }),
  statValueDiv: (props) => ({
    paddingTop: '0px !important',
    paddingBottom: `${theme.spacing(props.corporateView ? 4 : 0.5)}px !important`
  }),
  bottomPadding: {
    paddingBottom: theme.spacing(1.2)
  },
  topMargin: {
    marginTop: -theme.spacing(2)
  },
  popoverHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    padding: theme.spacing(0.5),
    paddingRight: theme.spacing(1),
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.common.white
  },
  closeIconDiv: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(0.5),
    backgroundColor: theme.palette.common.white,
    transition: 'background-color 0.2s ease-in-out',
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
      cursor: 'pointer'
    }
  }
}))

export interface StatCardMainGamifiedProps extends StatCardGamifiedProps {
  winRate?: number
  dollarsWon?: number
  corporateView?: boolean
}

function StatCardMainGamified(props: StatCardMainGamifiedProps) {
  const classes = useStyles(props)
  const theme = useTheme<Theme>()
  const {
    primaryTitle,
    loading,
    popupContent,
    primaryValue,
    statTitleLeft,
    statValueLeft,
    statTitleRight,
    statValueRight,
    popoverAnchor,
    leaderboardRankData,
    dollarsWon,
    showGamifiedCallouts,
    selectedEstimators,
    allEstimators,
    winRate,
    handleJoinClick
  } = props
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null)
  const donutSize: Size = {
    height: 99,
    width: 99
  }

  const open = Boolean(popoverAnchorEl)
  const [openGamifiedCollapse, setOpenGamifiedCollapse] = useState(false)

  const cardContent = (
    <div className={classes.content}>
      <div className={classes.leftContainer}>
        {popupContent && (
          <Popover
            classes={{ paper: classes.popoverPaper }}
            elevation={1}
            open={open}
            anchorEl={popoverAnchorEl}
            onClose={handleClose}
            anchorOrigin={
              popoverAnchor === 'right'
                ? {
                    vertical: 'center',
                    horizontal: 'right'
                  }
                : { vertical: 'center', horizontal: 'left' }
            }
            transformOrigin={
              popoverAnchor === 'right'
                ? {
                    vertical: 'center',
                    horizontal: 'left'
                  }
                : { vertical: 'center', horizontal: 'right' }
            }
          >
            <div className={classes.popoverHeader}>
              <div className={classes.closeIconDiv}>
                <CloseIcon onClick={() => handleClose()} />
              </div>
            </div>
            {popupContent}
          </Popover>
        )}
        <Grid container spacing={0} justifyContent="space-between">
          <Grid item xs={9}>
            <Typography variant="h2">{primaryTitle}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Button
              variant="text"
              className={classes.infoButton}
              icon={RemoveRedEyeIcon}
              onClick={handleClick}
              disabled={!popupContent}
            >
              <Typography variant="h6" weight={500} style={{ marginBottom: '-1px' }}>
                View
              </Typography>
            </Button>
          </Grid>
          <Grid item xs={12} direction="column" justifyContent="flex-end" style={{ marginTop: 0 }}>
            {loading || !primaryValue ? (
              <Skeleton
                width={'280px'}
                height={'90px'}
                style={{ marginBottom: theme.spacing(1.5) }}
                color={theme.paintscout.palette.brand.Neutral.P94}
              />
            ) : (
              <Typography showZeros={!primaryValue} variant="h1" className={classes.primaryValue}>
                {primaryValue}
              </Typography>
            )}
          </Grid>

          {showGamifiedCallouts ? (
            <Grid item xs={12}>
              <GamifiedCallout
                open={openGamifiedCollapse}
                selectedEstimators={selectedEstimators}
                allEstimators={allEstimators}
                onToggleCollapse={(_ev, open) => setOpenGamifiedCollapse((prev) => (open ? open : !prev))}
                topMargin={0}
                arrowPosition={'10%'}
                type="totalSold"
                data={leaderboardRankData}
                handleJoinClick={handleJoinClick}
              />
            </Grid>
          ) : (
            <Grid item>
              <Divider />
              <br />
              <br />
            </Grid>
          )}
        </Grid>

        {statTitleLeft && statTitleRight && (
          <Grid
            item
            container
            spacing={2}
            className={classNames({
              [classes.bottomPadding]: showGamifiedCallouts,
              [classes.topMargin]: !showGamifiedCallouts
            })}
          >
            <Grid item container direction="column" xs={6} className={classNames({ [classes.statValueDiv]: true })}>
              <Grid item>
                <Typography variant={'h5'} weight={500} noWrap>
                  {statTitleLeft}
                </Typography>
              </Grid>
              <Grid item>
                {loading || !statValueLeft ? (
                  <Skeleton width={'100%'} height={'40px'} color={theme.paintscout.palette.brand.Neutral.P94} />
                ) : (
                  <Typography variant={'h2'} className={classes.statExtrasValue}>
                    {statValueLeft}
                  </Typography>
                )}
              </Grid>
            </Grid>
            <Grid
              item
              container
              direction="column"
              xs={6}
              justifyContent="flex-end"
              alignItems="flex-end"
              className={classes.statValueDiv}
            >
              <Grid item>
                <Typography variant={'h5'} weight={500}>
                  {statTitleRight}
                </Typography>
              </Grid>
              <Grid item>
                {loading || !statValueRight ? (
                  <Skeleton width={'120px'} height={'40px'} color={theme.paintscout.palette.brand.Neutral.P94} />
                ) : (
                  <Typography variant={'h2'} className={classes.statExtrasValue}>
                    {statValueRight}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </div>
      <div className={classes.rightContainer}>
        <Grid container direction="row" alignItems="center" justifyContent="space-between" xs={12} spacing={0}>
          <Grid item xs={7}>
            <Tooltip
              icon={<HelpIcon fontSize="small" className={classes.helpIcon} />}
              interactive
              badgeRight={'-15%'}
              badgeTop="5%"
              content={<>The percentage of estimates that turn into accepted jobs.</>}
            >
              <Typography variant="h4" className={classes.donutTitle}>
                Win Rate
              </Typography>
            </Tooltip>
          </Grid>
          <Grid item xs={5}>
            <DonutChart loading={loading} value={winRate} maxValue={100} size={donutSize} strokeWidth={13} />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {showGamifiedCallouts ? (
            <div className={classes.gamifiedCalloutWrapper}>
              <GamifiedCallout
                selectedEstimators={selectedEstimators}
                allEstimators={allEstimators}
                open={openGamifiedCollapse}
                onToggleCollapse={(_ev, open) => setOpenGamifiedCollapse((prev) => (open ? open : !prev))}
                type="winRate"
                data={leaderboardRankData}
                topMargin={2.5}
                handleJoinClick={handleJoinClick}
              />
            </div>
          ) : (
            <Grid item>
              <Divider classes={{ root: classes.horizontalDivider }} />
            </Grid>
          )}
        </Grid>
        <Grid container direction="row" alignItems="center" justifyContent="space-between" xs={12} spacing={0}>
          <Grid item xs={7}>
            <Tooltip
              icon={<HelpIcon fontSize="small" className={classes.helpIcon} />}
              interactive
              badgeRight={'-10%'}
              badgeTop="5%"
              content={<>The percentage of the total estimated value that has been accepted as jobs.</>}
            >
              <Typography variant="h4" className={classes.donutTitle}>
                Dollars Won
              </Typography>
            </Tooltip>
          </Grid>
          <Grid item xs={5}>
            <DonutChart loading={loading} value={dollarsWon} maxValue={100} size={donutSize} strokeWidth={13} />
          </Grid>
        </Grid>
      </div>
    </div>
  )

  function handleClick(e: React.MouseEvent) {
    setPopoverAnchorEl(e.currentTarget)
  }

  function handleClose() {
    setPopoverAnchorEl(null)
  }

  return <StatCard {...props} classes={classes} cardContent={cardContent} variant="normal" />
}

export default StatCardMainGamified
